import { getAnalytics, logEvent, setAnalyticsCollectionEnabled } from "firebase/analytics";
import firebaseApp from "..";
import { VIRTUAL_NAME_ENERGY, VIRTUAL_SPEND_ITEM_CHAT } from "./constant";
import { useEffect } from "react";
import { hasConsent } from "@/store/useCookieConsentStore";
import { runWhenQuiet } from "@/util/dom";



let analytics
// 已同意但 GA 尚未初始化（初始化被推迟到页面空闲时）期间产生的事件，初始化后补发
let pendingEvents: LogEventRestParams[] = []

// 仅在浏览器端且用户已同意「分析类」cookie 时初始化 GA4，避免未经同意即采集数据。
export const initFirebaseAnalytics = () => {
  if (typeof window === 'undefined') return
  if (analytics || !hasConsent('analytics')) return
  analytics = getAnalytics(firebaseApp)
  pendingEvents.forEach(args => logEvent(analytics, ...args))
  pendingEvents = []
}

// gtag.js 的加载与执行是长任务，等页面加载完、用户暂停操作时再初始化，之前的事件由 pendingEvents 补发。
// 该 hook 被很多组件使用，全局只排一次，且不随组件卸载取消
let initScheduled = false
const scheduleInitFirebaseAnalytics = () => {
  if (analytics || initScheduled) return
  initScheduled = true
  runWhenQuiet(initFirebaseAnalytics)
}

// 运行时启停 GA4 自动采集：用户撤回同意后停止 page_view 等自动事件上报。
// 仅在已初始化时操作；未初始化（即从未同意）则无需处理。
export const setFirebaseAnalyticsEnabled = (enabled: boolean) => {
  if (typeof window === 'undefined') return
  if (enabled) {
    initFirebaseAnalytics()
    if (analytics) setAnalyticsCollectionEnabled(analytics, true)
  } else if (analytics) {
    setAnalyticsCollectionEnabled(analytics, false)
  }
}

type RestParams<T extends (...args: any) => any> = T extends (arg1: any, ...rest: infer R) => any ? R : never;
type LogEventRestParams = RestParams<typeof logEvent>;


// firebase 分析接入 具体信息访问下方地址
// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?sjid=9982097243333828742-AP&hl=zh-cn&client_type=gtag
export const useFirebaseAnalytics = () => {

  const fireBaseLogEvent = (...args: LogEventRestParams) => {
    if (!analytics) {
      // 已同意、仅是初始化尚未执行：暂存待补发；未同意则静默跳过
      if (hasConsent('analytics') && pendingEvents.length < 50) pendingEvents.push(args)
      return
    }
    logEvent(analytics, ...args)
  }

  const logEarnVirtualCurrency = (value: number) => {
    fireBaseLogEvent("earn_virtual_currency", {
      virtual_currency_name: VIRTUAL_NAME_ENERGY,
      value
    })
  }

  const logSpendVirtualCurrency = (option: { value: number, item_name: string, virtual_currency_name?: string }) => {
    fireBaseLogEvent("spend_virtual_currency", {
      virtual_currency_name: VIRTUAL_NAME_ENERGY,
      ...option,
    })
  }

  const logLogin = (method: string) => {
    fireBaseLogEvent("login", {
      method
    })
  }

  const logSearch = (search_term: string) => {
    fireBaseLogEvent("search", {
      search_term
    })
  }

  const logSelectContent = (option: { content_type: string, content_id: string }) => {
    fireBaseLogEvent("select_content", option)
  }

  const logShare = (option: { method: string, content_type: string, item_id: string }) => {
    fireBaseLogEvent("share", option)
  }

  const logSignUp = (option: { method: string }) => {
    fireBaseLogEvent("sign_up", option)
  }

  useEffect(() => { scheduleInitFirebaseAnalytics() }, [])



  return {
    fireBaseLogEvent,
    logEarnVirtualCurrency,
    logSpendVirtualCurrency,
    logLogin,
    logSearch,
    logSelectContent,
    logShare,
    logSignUp

  }
}

